import i18n from "../i18n";
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { formatCurrency, formatNumber, formatPct } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scale, Calculator, Save, Download, Sparkles, PawPrint } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { toast } from "sonner";

export default function BreakEvenPage() {
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [fixed, setFixed] = useState(50000);
    const [price, setPrice] = useState(250);
    const [varCost, setVarCost] = useState(100);
    const [result, setResult] = useState(null);
    const [saved, setSaved] = useState([]);
    const [label, setLabel] = useState("");
    const [species, setSpecies] = useState([]);
    const [selectedSp, setSelectedSp] = useState("");
    const [spBE, setSpBE] = useState(null);
    const [spLoading, setSpLoading] = useState(false);

    const load = async () => {
        const [f, s, sp] = await Promise.all([
            api.get("/farm").then(r => r.data),
            api.get("/break-even").then(r => r.data),
            api.get("/species").then(r => r.data),
        ]);
        setFarm(f); setSaved(s); setSpecies(sp);
    };
    useEffect(() => { load(); }, []);
    useEffect(() => { calculate(); }, []); // calculate is defined below

    const calculate = async () => {
        try {
            const { data } = await api.post("/break-even/calc", {
                fixed_costs: fixed, avg_unit_price: price, variable_cost_per_unit: varCost,
            });
            setResult(data);
        } catch (e) {
            console.error("Break-even calc failed:", e);
            toast.error("Calculation failed: " + (e.response?.data?.detail || e.message));
        }
    };
    const saveScenario = async () => {
        if (!result) return;
        await api.post("/break-even", { label: label || `Scenario ${new Date().toLocaleDateString()}`, ...result });
        toast.success("Scenario saved"); setLabel(""); load();
    };
    const del = async (s) => { if (window.confirm("Delete?")) { await api.delete(`/break-even/${s.id}`); load(); } };

    const useActualFixed = async () => {
        try {
            const { data } = await api.get("/finance/actual-fixed-costs");
            setFixed(Math.round(data.estimated_fixed));
            toast.success(`Fixed costs set to ${formatCurrency(data.estimated_fixed, farm.currency)} (payroll + monthly expenses)`);
        } catch (e) { toast.error("Failed to load actual costs"); }
    };

    const loadSpeciesBE = async (sid) => {
        setSelectedSp(sid); setSpLoading(true); setSpBE(null);
        try {
            const { data } = await api.get(`/species/${sid}/break-even`);
            setSpBE(data);
            // Auto-fill inputs from species data
            setFixed(Math.round(data.fixed_costs.total));
            setPrice(Math.round(data.avg_sale_price) || price);
            // Partial cost evidence must not silently become a complete manual input.
            if (data.calculation_status !== "INDICATIVE_PARTIAL_COSTS") {
                setVarCost(Math.round(data.variable_cost_per_unit.total));
            }
            setTimeout(calculate, 100);
            toast.success(`Loaded live data for ${data.species.name}`);
        } catch (e) { toast.error("Failed to load species data"); }
        setSpLoading(false);
    };

    const margin = Math.max(Number(price) - Number(varCost), 0);
    const marginPct = price > 0 ? (margin / Number(price) * 100) : 0;

    const chartData = useMemo(() => {
        const be = result?.break_even_units || 0;
        const maxUnits = Math.max(Math.ceil(be * 2.2), 100);
        const step = Math.max(Math.ceil(maxUnits / 30), 1);
        const rows = [];
        for (let u = 0; u <= maxUnits; u += step) {
            const revenue = u * price;
            const totalCost = Number(fixed) + u * Number(varCost);
            rows.push({ units: u, revenue, totalCost, profit: revenue - totalCost });
        }
        return rows;
    }, [fixed, price, varCost, result]);

    return (
        <div className="space-y-6" data-testid="break-even-page">
            <div className="flex items-center gap-3">
                <Scale className="w-8 h-8 text-primary" />
                <div>
                    <div className="tiny-label">{i18n.t("batch3.62c7fcf46e27")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.3551f3225c61")}</h1>
                    <p className="text-muted-foreground mt-1">{i18n.t("batch3.13fe19c2df86")}</p>
                </div>
            </div>

            {/* Per-Species Auto Calculator */}
            <Card className="p-6 bg-primary/5 border-primary/20">
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-semibold">{i18n.t("batch3.9b00e5c1e298")}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{i18n.t("batch3.6d6343ca92e0")}</p>
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="tiny-label">{i18n.t("batch3.83a7238826ab")}</Label>
                        <Select value={selectedSp} onValueChange={loadSpeciesBE}>
                            <SelectTrigger className="h-12 mt-2" data-testid="species-be-select"><SelectValue placeholder={i18n.t("batch3.ae61346ab23e")} /></SelectTrigger>
                            <SelectContent>{species.filter(s=>s.active !== false).map(s => <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    {spLoading && <div className="text-sm text-muted-foreground">{i18n.t("batch3.0856945b0938")}</div>}
                </div>

                {spBE && (
                    <div className="mt-6 space-y-4" data-testid="species-be-result">
                        {spBE.calculation_status === "INDICATIVE_PARTIAL_COSTS" && (
                            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="species-break-even-partial-warning">
                                <strong>{i18n.t("breakEven.partialTitle")}</strong> {i18n.t("breakEven.partialMessage")}
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl grid place-items-center text-2xl" style={{background: (spBE.species.color || "#eee") + "40"}}>{spBE.species.icon}</div>
                            <div>
                                <div className="font-bold text-lg">{spBE.species.name}</div>
                                <div className="text-xs text-muted-foreground">{spBE.active_animals} {i18n.t("batch3.4c71073f929b")} {formatPct(spBE.animal_share_pct)} {i18n.t("batch3.9a4e89db46e7")} {spBE.period}</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Card className="p-4">
                                <div className="tiny-label">{i18n.t("batch3.47cae30647fe")}</div>
                                <div className="kpi-value text-2xl font-bold mt-2">{formatCurrency(spBE.avg_sale_price, farm.currency)}</div>
                                <div className="text-xs text-muted-foreground mt-1">{spBE.units_sold_this_month} {i18n.t("batch3.824b30eb3d6f")}</div>
                            </Card>
                            <Card className="p-4">
                                <div className="tiny-label">{spBE.calculation_status === "INDICATIVE_PARTIAL_COSTS" ? i18n.t("breakEven.knownVariableCost") : i18n.t("batch3.a438f6b92752")}</div>
                                <div className="kpi-value text-2xl font-bold mt-2">{formatCurrency(spBE.variable_cost_per_unit.total, farm.currency)}</div>
                                <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch3.ff1928e5d29b")} {formatCurrency(spBE.variable_cost_per_unit.feed, farm.currency)} {i18n.t("batch3.5530e706d06b")} {formatCurrency(spBE.variable_cost_per_unit.medicine, farm.currency)}</div>
                            </Card>
                            <Card className="p-4">
                                <div className="tiny-label">{i18n.t("batch3.d63af7f176b1")}</div>
                                <div className="kpi-value text-2xl font-bold mt-2">{formatCurrency(spBE.fixed_costs.total, farm.currency)}</div>
                                <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch3.d45987441aa0")} {formatCurrency(spBE.fixed_costs.payroll_share, farm.currency)}</div>
                            </Card>
                            <Card className="p-4 bg-primary/10 border-primary/30">
                                <div className="tiny-label text-primary">{spBE.calculation_status === "INDICATIVE_PARTIAL_COSTS" ? i18n.t("breakEven.indicativeBreakEven") : i18n.t("batch3.0ef7d891503a")}</div>
                                <div className="kpi-value text-2xl font-bold mt-2 text-primary">{formatNumber(spBE.break_even_units, {fractionDigits: 0})} {i18n.t("batch3.917625318331")}</div>
                                <div className="text-xs text-muted-foreground mt-1">{formatCurrency(spBE.break_even_amount, farm.currency)}</div>
                            </Card>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <Card className="p-4">
                                <div className="tiny-label">{i18n.t("batch3.2f711885e0a7")}</div>
                                <div className={`kpi-value text-xl font-bold mt-2 ${spBE.contribution_margin >= 0 ? "text-primary" : "text-destructive"}`}>{formatCurrency(spBE.contribution_margin, farm.currency)}</div>
                                <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch3.7f9047479da7")} {formatPct(spBE.contribution_margin_pct)}</div>
                            </Card>
                            <Card className="p-4">
                                <div className="tiny-label">{i18n.t("batch3.a9f3f5b0b784")}</div>
                                <div className="kpi-value text-xl font-bold mt-2">{formatCurrency(spBE.actual_revenue, farm.currency)}</div>
                            </Card>
                            <Card className={`p-4 ${spBE.profit_or_loss >= 0 ? "bg-primary/5" : "bg-destructive/5"}`}>
                                <div className="tiny-label">{spBE.calculation_status === "INDICATIVE_PARTIAL_COSTS" ? i18n.t("breakEven.indicativeResult") : (spBE.profit_or_loss >= 0 ? i18n.t("breakEven.profit") : i18n.t("breakEven.loss"))}</div>
                                <div className={`kpi-value text-xl font-bold mt-2 ${spBE.profit_or_loss >= 0 ? "text-primary" : "text-destructive"}`}>{formatCurrency(Math.abs(spBE.profit_or_loss), farm.currency)}</div>
                            </Card>
                        </div>

                        <div className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                            {i18n.t("batch3.3e28eff6c2fc")} {spBE.fixed_costs.allocation_method === "employee_assignment" ? "Payroll uses employees explicitly assigned to this species" : "Payroll and other expenses fair-shared by animal count"}{i18n.t("batch3.300cb1a029ad")}
                        </div>
                    </div>
                )}
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <Card className="p-6 lg:col-span-2 space-y-4">
                    <div className="tiny-label">{i18n.t("batch3.4fa71d634f54")}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="tiny-label">{i18n.t("batch3.fe5505baf6c5")}{farm.currency})</Label>
                            <Input type="number" value={fixed} onChange={e => setFixed(e.target.value)} className="h-12 mt-2 font-mono text-lg" data-testid="fixed-costs-input"/>
                        </div>
                        <div>
                            <Label className="tiny-label">{i18n.t("batch3.689338b65b38")}{farm.currency})</Label>
                            <Input type="number" value={price} onChange={e => setPrice(e.target.value)} className="h-12 mt-2 font-mono text-lg" data-testid="unit-price-input"/>
                        </div>
                        <div>
                            <Label className="tiny-label">{i18n.t("batch3.1a16790b5c4f")}{farm.currency})</Label>
                            <Input type="number" value={varCost} onChange={e => setVarCost(e.target.value)} className="h-12 mt-2 font-mono text-lg" data-testid="var-cost-input"/>
                        </div>
                        <div className="grid grid-cols-2 gap-2 items-end">
                            <Button onClick={calculate} className="h-12" data-testid="calc-btn"><Calculator className="w-4 h-4 mr-2" /> {i18n.t("batch3.8e5903a8e4db")}</Button>
                            <Button onClick={useActualFixed} variant="outline" className="h-12" data-testid="use-actual-btn"><Download className="w-4 h-4 mr-2" /> {i18n.t("batch3.92cd1375d5cf")}</Button>
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="tiny-label">{i18n.t("batch3.2f711885e0a7")}</div>
                    <div className="kpi-value text-4xl font-bold mt-3">{formatCurrency(margin, farm.currency)}</div>
                    <div className="text-xs text-muted-foreground mt-2">{i18n.t("batch3.e83c8a8ffdc9")}</div>
                </Card>
                <Card className="p-6">
                    <div className="tiny-label">{i18n.t("batch3.f7c40075b47e")}</div>
                    <div className={`kpi-value text-4xl font-bold mt-3 ${marginPct >= 40 ? "text-primary" : marginPct >= 20 ? "text-amber-600" : "text-destructive"}`}>{marginPct.toFixed(0)}%</div>
                    <div className="text-xs text-muted-foreground mt-2">{i18n.t("batch3.7aab10488c30")}</div>
                </Card>
            </div>

            {result && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-6 bg-primary/5 border-primary/20">
                        <div className="tiny-label text-primary">{i18n.t("batch3.f9c30b15af01")}</div>
                        <div className="kpi-value text-5xl font-bold mt-3 text-primary">{formatNumber(result.break_even_units, {fractionDigits: 0})}</div>
                        <div className="text-sm text-muted-foreground mt-1">{i18n.t("batch3.29972f1aec9b")}</div>
                    </Card>
                    <Card className="p-6">
                        <div className="tiny-label">{i18n.t("batch3.f7af89907cdc")}</div>
                        <div className="kpi-value text-3xl font-bold mt-3">{formatCurrency(result.break_even_amount, farm.currency)}</div>
                    </Card>
                    <Card className="p-6">
                        <div className="tiny-label">{i18n.t("batch3.c1934178ea75")}</div>
                        <div className="flex gap-2 mt-2">
                            <Input value={label} onChange={e=>setLabel(e.target.value)} placeholder={i18n.t("batch3.d3d362b505e9")} className="h-10" data-testid="scenario-name-input"/>
                            <Button onClick={saveScenario} variant="outline" className="h-10" data-testid="save-scenario-btn"><Save className="w-4 h-4" /></Button>
                        </div>
                    </Card>
                </div>
            )}

            <Card className="p-6">
                <h3 className="text-xl font-semibold">{i18n.t("batch3.06d91444ec58")}</h3>
                <p className="text-sm text-muted-foreground mb-4">{i18n.t("batch3.7e505191686b")}</p>
                <div style={{ width: "100%", height: 380 }}>
                    <ResponsiveContainer>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="units" label={{ value: "Units", position: "insideBottom", offset: -4 }} />
                            <YAxis tickFormatter={v => `${farm.currency === "PHP" ? "₱" : ""}${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={v => formatCurrency(v, farm.currency)} />
                            <Legend />
                            <Line type="monotone" dataKey="profit" name="Profit" stroke="#40916C" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                            <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#1D4ED8" strokeWidth={3} dot={false} />
                            <Line type="monotone" dataKey="totalCost" name="Total Cost" stroke="#EC4899" strokeWidth={3} dot={false} />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" />
                            {result?.break_even_units > 0 && <ReferenceLine x={Math.round(result.break_even_units)} stroke="#40916C" strokeDasharray="4 4" label={{ value: "Break-Even", position: "top", fill: "#40916C", fontSize: 12 }} />}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            {saved.length > 0 && (
                <Card className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{i18n.t("batch3.de556948132e")}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {saved.map(s => (
                            <Card key={s.id} className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="font-semibold">{s.label}</div>
                                    <button className="text-xs text-destructive" onClick={() => del(s)}>×</button>
                                </div>
                                <div className="mt-2 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">{i18n.t("batch3.12748281e0f0")}</span><span className="font-mono font-semibold">{formatNumber(s.break_even_units, {fractionDigits: 0})}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">{i18n.t("batch3.35cf82f9d2a9")}</span><span className="font-mono font-semibold">{formatCurrency(s.break_even_amount, farm.currency)}</span></div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}
